import type { PaymentMethod } from '../../../types/order-calculation';
import type { SaleResponse, TicketOfficeState, TicketOfficeView } from '../../../types/ticket-office';
import type { GiftCardType, ProductVariant, Seats } from '../../../types/types';
import type { CashModalProps, Bill } from '../../components/CashModal';
import type { DiscountsModalProps } from '../../components/DiscountsModal';
import type { MessageModalProps } from '../../components/MessageModal';
import type { OrderSummaryProps } from '../../components/OrderSummary';
import type { OrderSummaryItemProps } from '../../components/OrderSummaryItem';
import type { RadioOption } from '../../components/Radio';
import type { SummaryItemModalProps } from '../../components/SummaryItemModal';
import type { MembershipPurchaseModalProps } from '../../components/MembershipPurchaseModal';
import type { MembershipRenewalModalProps } from '../../components/MembershipRenewalModal';
import { formatPrice } from '../../number-utils';
import { Api } from '../api';
import {
    addDiscount,
    removeDiscountCode,
    removeGiftCard,
    removeMemberCard,
    removeSerialTicketDiscount,
} from './discount-utils';
import { getDiscountsSectionProps } from './DiscountsSection';
import { getFiscalOpenSaleModalProps, openFiscalSale } from './FiscalOpenSale';
import { setGridView } from './grid-view-utils';
import { getOrderSummaryFooterProps } from './OrderSummaryFooter';
import { getPartialPaymentModal } from './PartialPaymentModal';
import {
    openProductModal,
    openSerialTicketModal,
    removeProduct,
    removeSerialTicketPurchase,
    removeGiftCardPurchase,
    syncProducts,
    openGiftCardModal,
} from './product-utils';
import { openTicketModal, removeAllSeats, setSeatQuantity, toggleSeatUpdater } from './seat-utils';

/**
 * Filters seats matching a specific ticket type and price.
 * Uses string comparison to avoid float precision issues.
 */
const getSeatsForGroup = (state: TicketOfficeState, typeId: number, unitPrice: number): Seats => {
    const priceStr = unitPrice.toFixed(2);
    return {
        numbered: state.seats.booked.filter((s) => s.type === typeId && parseFloat(s.price).toFixed(2) === priceStr),
        unnumbered: (state.seats.additional || []).filter(
            (s) => s.type === typeId && parseFloat(s.price).toFixed(2) === priceStr
        ),
    };
};

const getTicketsSection = (
    view: TicketOfficeView,
    seatsByType: Record<string, Seats>
): OrderSummaryProps['tickets'] => {
    const { state } = view;
    const { currencySymbol, lang, calc } = state;

    if (!calc.tickets.length) {
        return;
    }

    // Detect if any ticket type has multiple different prices
    const ticketTypeGroups = calc.tickets.reduce(
        (acc, ticket) => {
            if (!acc[ticket.name]) {
                acc[ticket.name] = new Set();
            }
            acc[ticket.name].add(ticket.unitPrice);
            return acc;
        },
        {} as Record<string, Set<number>>
    );

    const items = calc.tickets.map((ticket): OrderSummaryItemProps => {
        // Show unit price if this ticket type has multiple different prices
        const hasMixedPrices = ticketTypeGroups[ticket.name].size > 1;
        const name = hasMixedPrices
            ? `${ticket.quantity}x ${ticket.name} (${formatPrice(ticket.unitPrice, currencySymbol)})`
            : `${ticket.quantity}x ${ticket.name}`;

        // Get seats for this specific price group
        const seats = getSeatsForGroup(state, ticket.typeId, ticket.unitPrice);

        return {
            name,
            price: formatPrice(ticket.totalPrice, currencySymbol),
            onRemove: () => removeAllSeats(view, seats),
            onEdit: () => {
                const ticketType = state.ticketTypes.find((type) => type.id === ticket.typeId);
                openTicketModal(view, ticketType, { [ticket.typeId]: seats }, ticket.unitPrice);
            },
        };
    });

    const section: OrderSummaryProps['tickets'] = {
        title: lang.summary.tickets,
        items,
    };

    return section;
};

const getProductsSection = (view: TicketOfficeView): OrderSummaryProps['products'] => {
    const { state } = view;
    const { currencySymbol, lang, products, serialTicketTypes, giftCardTypes, cardTypes } = state;

    const items: OrderSummaryItemProps[] = [];

    for (const product of products) {
        for (const variant of product.variants) {
            if (!variant.quantity) {
                continue;
            }

            const totalPrice = variant.quantity * variant.unitPrice;

            items.push({
                name: `${variant.quantity}x ${product.name}` + (variant.name ? ` (${variant.name})` : ''),
                price: formatPrice(totalPrice, currencySymbol),
                onRemove: () => removeProduct(view, variant),
                onEdit: () => openProductModal(view, variant),
            });
        }
    }

    for (const type of serialTicketTypes) {
        if (!type.quantity) {
            continue;
        }

        const totalPrice = type.quantity * type.unitPrice;

        items.push({
            name: `${type.quantity}x ${type.name}`,
            price: formatPrice(totalPrice, currencySymbol),
            onRemove: () => removeSerialTicketPurchase(view, type),
            onEdit: () => openSerialTicketModal(view, type),
        });
    }

    for (const type of giftCardTypes) {
        if (!type.quantity) {
            continue;
        }

        const totalPrice = type.quantity * type.unitPrice;

        // For open gift cards with custom price, show the price in the name
        const displayName =
            type.allow_custom_value && type.unitPrice > 0
                ? `${type.quantity}x ${type.name} (${formatPrice(type.unitPrice, currencySymbol)})`
                : `${type.quantity}x ${type.name}`;

        items.push({
            name: displayName,
            price: formatPrice(totalPrice, currencySymbol),
            onRemove: () => removeGiftCardPurchase(view, type),
            onEdit: () => openGiftCardModal(view, type),
        });
    }

    // Add purchased memberships from calculation response
    if (state.calc?.memberships) {
        for (const membership of state.calc.memberships) {
            const cardType = cardTypes.find((ct) => ct.id === membership.cardTypeId);
            if (!cardType) {
                continue;
            }

            // For new memberships, server has no member data yet, so check local state
            const localMembership =
                !membership.member &&
                state.purchasedMemberships?.find(
                    (pm) => pm.cardTypeId === membership.cardTypeId && !membership.isRenewal
                );

            const memberInfo = membership.member
                ? membership.member.email || membership.member.name || 'Unknown'
                : localMembership?.email || localMembership?.name || 'Unknown';
            const renewalIndicator = membership.isRenewal ? ' (PIKENDUS)' : '';

            items.push({
                name: `${membership.name} - ${memberInfo}${renewalIndicator}`,
                price: formatPrice(Number(membership.unitPrice), currencySymbol),
                onRemove: () => {
                    // TODO: Implement membership removal from cart
                    // This will be implemented when we add the cart removal API
                },
                // TODO: Add edit functionality if needed
            });
        }
    }

    // Also add locally added memberships that might not be in calc yet
    if (state.purchasedMemberships) {
        for (const membership of state.purchasedMemberships) {
            // Check if this membership is already in calc.memberships
            // During calculation, new memberships don't have member data yet
            // So we need to match on cardTypeId and check if we already have one without member data
            const isInCalc = state.calc?.memberships?.some((m) => {
                // Same card type
                if (m.cardTypeId !== membership.cardTypeId) {
                    return false;
                }

                // If the calc membership has no member data, it's likely the same new membership
                if (!m.member) {
                    return true;
                }

                // If it has member data, check if emails match
                return m.member.email === membership.email;
            });

            if (!isInCalc) {
                const cardType = cardTypes.find((ct) => ct.id === membership.cardTypeId);
                if (!cardType) {
                    continue;
                }

                const memberInfo = membership.email || membership.name || 'Unknown';
                const renewalIndicator = membership.isRenewal ? ' (PIKENDUS)' : '';

                items.push({
                    name: `${cardType.name} - ${memberInfo}${renewalIndicator}`,
                    price: formatPrice(cardType.membership_price, currencySymbol),
                    onRemove: () => {
                        // Remove membership from state
                        state.purchasedMemberships = state.purchasedMemberships?.filter((m) => m !== membership);
                        view.update();
                        syncProducts(view);
                    },
                    // TODO: Add edit functionality if needed
                });
            }
        }
    }

    if (!items.length) {
        return;
    }

    const section: OrderSummaryProps['products'] = {
        title: lang.summary.products,
        items,
    };

    return section;
};

const getSummaryItemModal = (
    view: TicketOfficeView,
    seatsByType: Record<string, Seats>
): SummaryItemModalProps | undefined => {
    const { state } = view;
    const summaryItemModalValue = state.summaryItemModalType;

    if (summaryItemModalValue == 'ticket') {
        return getTicketSummaryItemModal(view, seatsByType);
    } else if (summaryItemModalValue == 'product') {
        return getProductSummaryItemModal(view);
    } else if (summaryItemModalValue === 'serialTicket') {
        return getSerialTicketSummaryItemModal(view);
    } else if (summaryItemModalValue === 'giftCard') {
        return getGiftCardSummaryItemModal(view);
    } else {
        return undefined;
    }
};

const getTicketSummaryItemModal = (
    view: TicketOfficeView,
    seatsByType: Record<string, Seats>
): SummaryItemModalProps => {
    const { state, update } = view;
    const { lang, currencySymbol } = state;

    const ticketType = state.ticketTypes.find((type) => type.id === state.summaryItemModalTicketTypeId);

    if (!ticketType) {
        console.error('Ticket type not found:', state.summaryItemModalTicketTypeId);
        return {
            show: false,
            toggleModal: () => {
                state.summaryItemModalType = undefined;
                state.summaryItemModalPrice = undefined;
                update();
            },
        } as SummaryItemModalProps;
    }

    // Reconstruct filtered seats using stored price to ensure we only operate on the specific price group
    const filteredSeats =
        state.summaryItemModalPrice !== undefined
            ? getSeatsForGroup(state, ticketType.id, state.summaryItemModalPrice)
            : seatsByType[ticketType.id];

    // Build filtered seatsByType for this price group only
    const filteredSeatsByType = { [ticketType.id]: filteredSeats };

    const count = ticketType.modalCount;
    const unitPrice = state.summaryItemModalPrice ?? parseFloat(ticketType.price);
    const totalPrice = count * unitPrice;

    return {
        title: ticketType.name,
        show: true,
        quantityLabel: lang.common.quantity,
        closeLabel: lang.common.close,
        saveLabel: lang.common.save,
        priceLabel: lang.summary.total,
        price: formatPrice(totalPrice, currencySymbol),
        quantityInput: {
            value: count,
            min: 0,
            inputClass: count ? '' : 'number-input__input--faded',
            onChange: (newQuantity: number) => {
                ticketType.modalCount = newQuantity;
                update();
            },
        },
        onSave: () => {
            setSeatQuantity(view, ticketType, filteredSeatsByType, ticketType.modalCount, state.summaryItemModalPrice);
            state.summaryItemModalType = undefined;
            state.summaryItemModalPrice = undefined;
            update();
        },
        onRemove: () => {
            removeAllSeats(view, filteredSeats);
            state.summaryItemModalType = undefined;
            state.summaryItemModalPrice = undefined;
            update();
        },
        toggleModal: () => {
            state.summaryItemModalType = undefined;
            state.summaryItemModalPrice = undefined;
            update();
        },
    };
};

const getProductSummaryItemModal = (view: TicketOfficeView): SummaryItemModalProps => {
    const { state, update } = view;
    const { lang, currencySymbol, products } = state;

    const variant = getSummaryItemModalVariant(state);
    const totalPrice = state.modalQuantity * state.modalUnitPrice;

    const product = products.filter((p) => p.variants.includes(variant))[0];

    return {
        title: `${variant.quantity}x ${product.name}` + (variant.name ? ` (${variant.name})` : ''),
        show: true,
        quantityLabel: lang.common.quantity,
        closeLabel: lang.common.close,
        saveLabel: lang.common.save,
        priceLabel: lang.summary.total,
        unitPriceLabel: lang.common.unitPrice,
        price: formatPrice(totalPrice, currencySymbol),
        // TODO: actually fix and re-enable
        // priceField: {
        //     inputClass: 'hide-controls font-semibold pr-[48px]',
        //     value: variant.modalUnitPrice.toString().replace('.', ','),
        //     inputmode: 'numeric',
        //     prefix: currencySymbol,
        //     onChange: (value) => {
        //         if ((value.endsWith(',')) && !(value.indexOf(',') < (value.length - 1)) || value.endsWith(',0')) {
        //             return
        //         }
        //
        //         const comaReplacedValue = value.replace(',', '.')
        //
        //         if (parseFloat(comaReplacedValue)) {
        //             variant.modalUnitPrice = Math.max(0, parseFloat(comaReplacedValue));
        //             update()
        //         } else {
        //             variant.modalUnitPrice = 0;
        //             update()
        //         }
        //     },
        //     onClear: variant.modalUnitPrice !== variant.initialUnitPrice && (() => {
        //         variant.modalUnitPrice = variant.initialUnitPrice
        //         update()
        //     })
        // },
        quantityInput: {
            value: state.modalQuantity,
            onChange: (newQuantity): void => {
                state.modalQuantity = newQuantity;
                update();
            },
            min: 0,
            max: variant.maxQuantity,
        },
        onSave: () => {
            variant.unitPrice = state.modalUnitPrice;
            variant.quantity = state.modalQuantity;
            state.summaryItemModalType = undefined;
            update();
            syncProducts(view);
        },
        onRemove: () => {
            removeProduct(view, variant);
            state.summaryItemModalType = undefined;
            update();
        },
        toggleModal: () => {
            state.summaryItemModalType = undefined;
            update();
        },
    };
};

const getSummaryItemModalVariant = (state: TicketOfficeState): ProductVariant | undefined => {
    const { products } = state;

    for (const product of products) {
        for (const variant of product.variants) {
            if (variant.id === state.summaryItemModalProductVariantId) {
                return variant;
            }
        }
    }
};

const getSerialTicketSummaryItemModal = (view: TicketOfficeView): SummaryItemModalProps => {
    const { state, update } = view;
    const { lang, currencySymbol, products } = state;

    const type = state.serialTicketTypes.find((type) => type.id === state.summaryItemModalSerialTicketTypeId);

    const totalPrice = state.modalQuantity * type.unitPrice;

    return {
        title: `${type.quantity}x ${type.name}`,
        show: true,
        quantityLabel: lang.common.quantity,
        closeLabel: lang.common.close,
        saveLabel: lang.common.save,
        priceLabel: lang.summary.total,
        price: formatPrice(totalPrice, currencySymbol),
        quantityInput: {
            value: state.modalQuantity,
            onChange: (newQuantity): void => {
                state.modalQuantity = newQuantity;
                update();
            },
            min: 0,
        },
        onSave: () => {
            type.quantity = state.modalQuantity;
            state.summaryItemModalType = undefined;
            update();
            syncProducts(view);
        },
        onRemove: () => {
            removeSerialTicketPurchase(view, type);
            state.summaryItemModalType = undefined;
            update();
        },
        toggleModal: () => {
            state.summaryItemModalType = undefined;
            update();
        },
    };
};

const getGiftCardSummaryItemModal = (view: TicketOfficeView): SummaryItemModalProps => {
    const { state, update } = view;
    const { lang, currencySymbol, products } = state;

    // Find the correct gift card type instance
    let type: GiftCardType | undefined;
    if (state.summaryItemModalGiftCardCompositeKey) {
        // For open gift cards with custom prices, find by matching both ID and price
        type = state.giftCardTypes.find((t) => {
            if (t.id !== state.summaryItemModalGiftCardTypeId) {
                return false;
            }
            const compositeKey = `${t.id}_${Math.round(t.unitPrice * 100)}`;
            return compositeKey === state.summaryItemModalGiftCardCompositeKey;
        });
    } else {
        // For regular gift cards, find by ID
        type = state.giftCardTypes.find((t) => t.id === state.summaryItemModalGiftCardTypeId);
    }

    if (!type) {
        // Fallback to first matching ID if exact match not found
        type = state.giftCardTypes.find((t) => t.id === state.summaryItemModalGiftCardTypeId);
    }

    if (!type) {
        console.error('Gift card type not found', state.summaryItemModalGiftCardTypeId);
        return {} as SummaryItemModalProps;
    }

    const totalPrice = state.modalQuantity * state.modalUnitPrice;

    return {
        title: `${type.quantity}x ${type.name}`,
        show: true,
        quantityLabel: lang.common.quantity,
        closeLabel: lang.common.close,
        saveLabel: lang.common.save,
        priceLabel: lang.summary.total,
        price: formatPrice(totalPrice, currencySymbol),
        quantityInput: {
            value: state.modalQuantity,
            onChange: (newQuantity): void => {
                state.modalQuantity = newQuantity;
                update();
            },
            min: 0,
        },
        onSave: () => {
            type.quantity = state.modalQuantity;
            state.summaryItemModalType = undefined;
            update();
            syncProducts(view);
        },
        onRemove: () => {
            removeGiftCardPurchase(view, type);
            state.summaryItemModalType = undefined;
            update();
        },
        toggleModal: () => {
            state.summaryItemModalType = undefined;
            update();
        },
    };
};

const getDiscountsSection = (view: TicketOfficeView): OrderSummaryProps['discounts'] => {
    const { state } = view;
    const { currencySymbol, lang, calc } = state;

    const items: OrderSummaryItemProps[] = [];

    for (const payment of calc.serialTicketPayments) {
        items.push({
            name: `${lang.discounts.serialTicket} ${payment.number}`,
            price: formatPrice(-payment.amount, currencySymbol),
            onRemove: () => removeSerialTicketDiscount(view, payment.serialTicketId),
        });
    }

    if (calc.memberCard) {
        items.push({
            name: `${calc.memberCard.name} ${calc.memberCard.number}`,
            price: formatPrice(-calc.memberCard.discount, currencySymbol),
            onRemove: () => removeMemberCard(view),
        });
    }

    for (const payment of calc.giftCardPayments) {
        items.push({
            name: `${lang.discounts.giftCard} ${payment.number}`,
            price: formatPrice(-payment.amount, currencySymbol),
            onRemove: () => removeGiftCard(view, payment.number),
        });
    }

    if (calc.discountCodes) {
        for (const code of calc.discountCodes) {
            items.push({
                name: `${lang.discounts.discountCode} ${code.number}`,
                price: formatPrice(-code.amount, currencySymbol),
                onRemove: () => removeDiscountCode(view, code.number),
            });
        }
    }

    if (!items.length) {
        return;
    }

    const section: OrderSummaryProps['discounts'] = {
        title: lang.summary.discounts,
        items,
    };

    return section;
};

// TODO Unused
const getSubmitText = (state: TicketOfficeState): string => {
    const { lang, reservationId, paymentMethod } = state;

    if (paymentMethod === 'booking') {
        return reservationId ? lang.summary.updateBooking : lang.summary.reserve;
    }

    return lang.summary.confirm;
};

const getDiscountsButton = (view: TicketOfficeView): { text: string; onClick?: () => void } => {
    const { state, update } = view;
    const { lang } = state;

    return {
        text: lang.summary.discounts,
        onClick: () => {
            state.isDiscountsModalOpen = true;
            update();
        },
    };
};

const getViewButton = (view: TicketOfficeView): { text: string; onClick?: () => void } => {
    const { state, update } = view;
    const { lang } = state;

    return {
        text: state.isGridView ? lang.summary.gotoTicket : lang.summary.gotoGrid,
        onClick: () => setGridView(view, !state.isGridView),
    };
};

const getCashModalProps = (view: TicketOfficeView): CashModalProps => {
    const { state, update } = view;
    const { lang, currencySymbol, calc } = state;

    const paidValue = state.cashModal.paidValue;
    const fieldValue = state.cashModal.fieldValue;

    const formattedTotal = formatPrice(calc.toPay, currencySymbol);
    const formattedReturn = formatPrice(Math.max(0, state.cashModal.paidValue - calc.toPay), currencySymbol);

    const onPaidAmountChange = (value: string) => {
        state.cashModal.fieldValue = value;
        if ((value.endsWith(',') && value.indexOf(',') == value.length - 1) || value.endsWith(',0')) {
            update();
            return;
        }

        const comaReplacedValue = value.replace(',', '.');

        if (parseFloat(comaReplacedValue)) {
            const newValue = Math.max(0, Math.round(parseFloat(comaReplacedValue) * 100) / 100);
            state.cashModal.paidValue = newValue;
            state.cashModal.fieldValue = newValue.toString().replace('.', ',');
        } else {
            state.cashModal.paidValue = 0;
            state.cashModal.fieldValue = '0';
        }
        update();
    };

    return {
        title: state.lang.sections.cashPayment,
        confirmButtonLabel: lang.summary.confirmPaymentButtonLabel,
        cancelButtonLabel: lang.common.cancel,
        openRegisterLabel: lang.summary.openRegister,
        show: state.isCashModalOpen,
        keys: ['9', '8', '7', '6', '5', '4', '3', '2', '1', '0', ','],
        bills: [100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05, 0.01].map((value): Bill => ({
            image: state.billImages[value],
            label: formatPrice(value, currencySymbol),
            value,
        })),
        toPay: {
            label: lang.summary.toPay,
            value: formattedTotal,
        },
        toReturn: {
            label: lang.summary.toReturn,
            value: formattedReturn,
        },
        paidAmountField: {
            inputClass: 'hide-controls font-semibold pr-[48px]',
            value: state.cashModal.fieldValue,
            inputmode: 'numeric',
            prefix: currencySymbol,
            onChange: onPaidAmountChange,
            onClear:
                !!paidValue &&
                (() => {
                    state.cashModal.paidValue = 0;
                    state.cashModal.fieldValue = '0';
                    update();
                }),
        },
        onBillClick: (bill) => {
            onPaidAmountChange((paidValue + bill).toString());
        },
        onKeyClick: (key) => {
            onPaidAmountChange(fieldValue + key);
        },
        toggleModal: () => {
            state.isCashModalOpen = !state.isCashModalOpen;
            update();
        },
        onConfirm: () => {
            state.isCashModalOpen = false;
            submit(view);
            update();
        },
        onOpenRegister: async () => {
            const response = await Api.post(state.urls.openRegister, state.csrfToken);

            if (!response.ok) {
                window.toastr.error(lang.errors.registerFailedToOpen);
            }
        },
    };
};

const toggleConfirmationModal = (view: TicketOfficeView): void => {
    view.state.isConfirmationModalOpen = !view.state.isConfirmationModalOpen;
    view.update();
};

const toggleCashModal = (view: TicketOfficeView): void => {
    view.state.isCashModalOpen = !view.state.isCashModalOpen;
    view.update();
};

const togglePartialPaymentModal = (view: TicketOfficeView, paymentMethodValue: string): void => {
    view.state.isPartialPaymentModalOpen = !view.state.isPartialPaymentModalOpen;
    view.state.partialPaymentModal = {
        paymentMethodValue,
        amountValue: 0,
    };
    view.update();
};

const submit = async (view: TicketOfficeView): Promise<void> => {
    const { state } = view;
    const { lang, calc } = state;

    if (calc.toPay < 0) {
        window.toastr.error(lang.errors.noNegativeTotal);
        return;
    }

    toggleSeatUpdater(view, false);

    const isBooking = state.paymentMethod === 'booking';
    const url = isBooking ? state.urls.book : state.urls.sell;

    const reqBody: Record<string, unknown> = {
        customer: state.customer,
    };

    if (isBooking) {
        reqBody.reservation_id = state.reservationId;
    } else {
        reqBody.payment_method = state.paymentMethod;
        reqBody.card_terminal_id = state.cardTerminalId ?? null;
        reqBody.sendEmail = state.sendEmail;
        reqBody.print = state.print;
        reqBody.print_receipt = state.printReceipt;

        if (state.reservationId) {
            reqBody.reservation_id = state.reservationId;
        }
    }

    state.isSubmitting = true;
    view.update();

    const response = await Api.postJson(url, state.csrfToken, reqBody);

    state.isSubmitting = false;

    if (!response.ok) {
        window.toastr.error(lang.errors.submitFailed);
    } else {
        const json: SaleResponse = await response.json();

        if (!json.success) {
            if (json.outdated) {
                state.outdatedMessage = json.outdated;
                view.update();
            } else if (json.error) {
                window.toastr.error(json.error);
            } else if (!json.fiscal_open) {
                window.toastr.error(lang.errors.submitFailed);
            }

            // The EFR has not signed the sale: it stays open at this cash register.
            if (json.fiscal_open) {
                openFiscalSale(view, json.fiscal_open);
            }
        } else {
            if (json.redirectUrl) {
                window.location.assign(json.redirectUrl);
            } else {
                window.location.reload();
            }

            return;
        }
    }

    toggleSeatUpdater(view, true);
};

/**
 * Completes a sale with nothing left to pay: it is sent without a payment method and records only the payments that
 * cover it. The server takes it only while nothing is left to pay.
 */
export const completeSale = (view: TicketOfficeView): void => {
    view.state.paymentMethod = null;
    view.state.cardTerminalId = null;
    void submit(view);
};

export const onSubmit = (view: TicketOfficeView, paymentMethod: RadioOption): void | Promise<void> => {
    const { state, update } = view;
    state.paymentMethod = paymentMethod.value;
    state.cardTerminalId = paymentMethod.terminal ?? null;

    if (paymentMethod.triggersPartialPayment) {
        togglePartialPaymentModal(view, paymentMethod.value);
    } else {
        switch (paymentMethod.value) {
            case 'card':
                if (state.askForCardPaymentConfirmation) {
                    toggleConfirmationModal(view);
                } else {
                    submit(view);
                }
                break;

            case 'cash':
                if (state.showCashModal) {
                    toggleCashModal(view);
                } else {
                    submit(view);
                }
                break;

            default:
                submit(view);
        }
    }

    update();
};

const getDiscountsModalProps = (view: TicketOfficeView): DiscountsModalProps => {
    const { state, update } = view;
    const { lang } = state;

    return {
        title: lang.summary.discounts,
        show: true,
        closeLabel: lang.common.close,
        addLabel: lang.common.add,
        isSaveDisabled: !state.discountModalValue,
        toggleModal: (): void => {
            state.isDiscountsModalOpen = !state.isDiscountsModalOpen;
            state.discountModalValue = undefined;
            update();
        },
        onSave: (): void => {
            if (state.discountModalValue) {
                addDiscount(view, state.discountModalValue);
            }
            state.isDiscountsModalOpen = false;
            update();
        },
        discounts: getDiscountsSectionProps(view),
    };
};

/**
 * A terminal payment that came back unpaid is closed with OK; a page that no longer matches its cash register
 * offers to reload, or to back out.
 */
const getMessageModalProps = (view: TicketOfficeView): MessageModalProps | undefined => {
    const { state, update } = view;
    const { lang } = state;

    if (state.terminalPaymentFailed) {
        return {
            ...state.terminalPaymentFailed,
            confirmLabel: lang.terminals.ok,
            onConfirm: (): void => {
                state.terminalPaymentFailed = null;
                update();
            },
        };
    }

    if (state.outdatedMessage) {
        return {
            ...state.outdatedMessage,
            confirmLabel: lang.terminals.reload,
            onConfirm: (): void => window.location.reload(),
            cancelLabel: lang.common.cancel,
            onCancel: (): void => {
                state.outdatedMessage = null;
                update();
            },
        };
    }

    return undefined;
};

const getConfirmationModalProps = (view: TicketOfficeView) => {
    const { state, update } = view;
    const { lang } = state;

    return {
        title: lang.summary.confirmPaymentTitle,
        confirmButtonLabel: lang.summary.yes,
        cancelButtonLabel: lang.summary.no,
        show: true,
        toggleModal: (): void => {
            toggleConfirmationModal(view);
            update();
        },
        submit: (): void => {
            submit(view);
        },
    };
};

const getMembershipPurchaseModalProps = (view: TicketOfficeView): MembershipPurchaseModalProps => {
    const { state, update } = view;
    const { lang, currencySymbol, cardTypes, urls } = state;

    const cardTypeId = state.membershipPurchaseModalCardTypeId;
    const cardType = cardTypes.find((ct) => ct.id === cardTypeId) || null;

    return {
        show: state.isMembershipPurchaseModalOpen || false,
        toggleModal: () => {
            state.isMembershipPurchaseModalOpen = false;
            state.membershipPurchaseModalCardTypeId = undefined;
            update();
        },
        cardType,
        lang: {
            title: lang.membership.buy + ' {name}',
            emailLabel: lang.customer.email + ' (' + lang.membership.optional + ')',
            emailPlaceholder: lang.customer.email,
            nameLabel: lang.customer.name + ' (' + lang.membership.optional + ')',
            namePlaceholder: lang.customer.name,
            existingMemberNote: lang.membership.existingMemberNote,
            addToCartLabel: lang.common.add,
            cancelLabel: lang.common.cancel,
            searchingLabel: lang.membership.searching,
            monthsLabel: lang.membership.months,
        },
        currencySymbol,
        searchMemberUrl: '/api/members/search',
        onAddToCart: async (data) => {
            // Initialize purchasedMemberships if it doesn't exist
            if (!state.purchasedMemberships) {
                state.purchasedMemberships = [];
            }

            // Add new membership purchase
            const membership = { ...data, isRenewal: false };
            state.purchasedMemberships.push(membership);

            // Sync with backend, which turns back an address the mail service would refuse
            const refusal = await syncProducts(view);

            if (refusal) {
                state.purchasedMemberships = state.purchasedMemberships.filter((m) => m !== membership);
                return refusal;
            }

            // Close modal
            state.isMembershipPurchaseModalOpen = false;
            state.membershipPurchaseModalCardTypeId = undefined;
            update();

            return undefined;
        },
    };
};

const getMembershipRenewalModalProps = (view: TicketOfficeView): MembershipRenewalModalProps => {
    const { state, update } = view;
    const { lang, currencySymbol, cardTypes, urls } = state;

    const cardTypeId = state.membershipRenewalModalCardTypeId;
    const cardType = cardTypes.find((ct) => ct.id === cardTypeId) || null;

    return {
        show: state.isMembershipRenewalModalOpen || false,
        toggleModal: () => {
            state.isMembershipRenewalModalOpen = false;
            state.membershipRenewalModalCardTypeId = undefined;
            update();
        },
        cardType,
        lang: {
            title: lang.membership.renew + ' {name}',
            emailLabel: lang.customer.email,
            emailPlaceholder: lang.customer.email,
            searchingLabel: lang.membership.searching,
            monthsLabel: lang.membership.months,
            addToCartLabel: lang.common.add,
            cancelLabel: lang.common.cancel,
            noMemberFoundLabel: lang.membership.noMemberFound,
            memberInfoLabel: lang.membership.memberInfo,
            existingCardsLabel: lang.membership.existingCards,
            validUntilLabel: lang.membership.validUntil,
            expiredLabel: lang.membership.expired,
            activeLabel: lang.membership.active,
            renewableLabel: lang.membership.renewable,
            notRenewableLabel: lang.membership.notRenewable,
            newExpiryDateLabel: lang.membership.newExpiryDate,
            renewalLabel: lang.membership.renewal,
        },
        currencySymbol,
        searchMemberUrl: '/api/members/search',
        onAddToCart: (data) => {
            // Initialize purchasedMemberships if it doesn't exist
            if (!state.purchasedMemberships) {
                state.purchasedMemberships = [];
            }

            // Add renewal
            state.purchasedMemberships.push({
                cardTypeId: data.cardTypeId,
                email: data.email,
                name: data.name,
                memberId: data.memberId,
                isRenewal: true,
                renewFromCardId: data.renewFromCardId,
                previousValidTo: data.previousValidTo,
            });

            // Close modal
            state.isMembershipRenewalModalOpen = false;
            state.membershipRenewalModalCardTypeId = undefined;
            update();

            // Sync with backend
            syncProducts(view);
        },
    };
};

export const getOrderSummary = (view: TicketOfficeView, seatsByType: Record<string, Seats>): OrderSummaryProps => {
    const { state } = view;
    const { lang } = state;

    const props: OrderSummaryProps = {
        title: lang.summary.title,
        tickets: getTicketsSection(view, seatsByType),
        products: getProductsSection(view),
        discounts: getDiscountsSection(view),
        footer: getOrderSummaryFooterProps(view),
        discountsButton: getDiscountsButton(view),
        viewButton: getViewButton(view),
        confirmationModal: state.isConfirmationModalOpen && getConfirmationModalProps(view),
        cashModal: getCashModalProps(view),
        discountsModal: state.isDiscountsModalOpen && getDiscountsModalProps(view),
        partialPaymentModal: state.isPartialPaymentModalOpen && getPartialPaymentModal(view),
        summaryItemModal: getSummaryItemModal(view, seatsByType),
        membershipPurchaseModal: state.isMembershipPurchaseModalOpen && getMembershipPurchaseModalProps(view),
        membershipRenewalModal: state.isMembershipRenewalModalOpen && getMembershipRenewalModalProps(view),
        fiscalOpenSaleModal: getFiscalOpenSaleModalProps(view),
        messageModal: getMessageModalProps(view),
    };

    if (!state.hasWorkday) {
        props.cashRegisterWarning = `${lang.errors.noActiveWorkday}!`;
    }

    return props;
};
